import { useState, useEffect, useCallback, useMemo } from 'react';
import { BaseFormModal } from '@/components/BaseFormModal';
import {
  THEME_DAY_COOKIE_VALUE,
  THEME_NIGHT_BLUE_COOKIE_VALUE,
  THEME_NIGHT_GREY_COOKIE_VALUE,
} from '@/utils/theme';
import { updateThemeColor, getCurrentThemeColor } from '@/services/theme-service';

interface ThemeOption {
  value: string;
  label: string;
  image: string;
}

export interface ThemePickerModalProps {
  open: boolean;
  theme?: string;
  onCancel: () => void;
}

export function ThemePickerModal({
  open,
  theme,
  onCancel,
}: ThemePickerModalProps) {
  const [selectedTheme, setSelectedTheme] = useState(getCurrentThemeColor);

  const themes: ThemeOption[] = useMemo(
    () => [
      { value: THEME_DAY_COOKIE_VALUE, label: 'Day', image: '/Content/Customize/img/dayTheme.png' },
      { value: THEME_NIGHT_BLUE_COOKIE_VALUE, label: 'Night Blue', image: '/Content/Customize/img/blueTheme.png' },
      { value: THEME_NIGHT_GREY_COOKIE_VALUE, label: 'Night Grey', image: '/Content/Customize/img/greyTheme.png' },
    ],
    []
  );

  // Reset selected theme when modal opens
  useEffect(() => {
    if (open) {
      setSelectedTheme(getCurrentThemeColor());
    }
  }, [open]);

  const selectTheme = useCallback(
    (themeValue: string) => {
      setSelectedTheme(themeValue);
      updateThemeColor(themeValue);
      onCancel();
    },
    [onCancel]
  );

  return (
    <BaseFormModal
      open={open}
      title="Select Theme"
      theme={theme}
      dialogClass="kstc-dialog--large"
      showSave={false}
      cancelText="Close"
      onCancel={onCancel}
    >
      <div className="kstc-radio-group kstc-theme-picker-options">
        {themes.map((t) => (
          <label
            key={t.label}
            className="kstc-radio-label kstc-theme-picker-option"
            onClick={() => selectTheme(t.value)}
          >
            <input
              type="radio"
              name="kstc_theme_pick"
              value={t.value}
              checked={selectedTheme === t.value}
              readOnly
            />
            <span className="kstc-radio-text">{t.label}</span>
            <img src={t.image} alt={t.label} className="kstc-theme-preview" />
          </label>
        ))}
      </div>
    </BaseFormModal>
  );
}

(window as any).ThemePickerModalReact = ThemePickerModal;
